/**
 * Pure vertical-layout math for the host -> hubs -> phones USB topology
 * fan-out graph. Ports the layout algorithm from the accepted mockup
 * (.claude/.scratch/host-topology-mockup.html) — deliberately decoupled
 * from the view-model shape (topologyAdapter.ts) so it can be unit-tested
 * on plain fixtures without constructing a full TopologyViewModel.
 *
 * Algorithm: stack each hub's devices top-to-bottom (chip height + gap),
 * a hub's center-y is the mean of its devices' center-ys, and the host's
 * center-y is the mean of the hubs' center-ys. Height grows with the
 * number of devices — never a fixed/zero size.
 */

export const CHIP_H = 22;
export const CHIP_GAP = 4;
export const GROUP_GAP = 16;
export const START_Y = 18;

export interface LayoutHubInput {
  id: string;
}

export interface LayoutDeviceInput {
  serial: string;
  hubId: string;
}

export interface DeviceLayoutNode {
  serial: string;
  hubId: string;
  yTop: number;
  cy: number;
}

export interface HubLayoutNode {
  id: string;
  yTop: number;
  cy: number;
  count: number;
}

export interface TopologyLayout {
  hubs: HubLayoutNode[];
  devices: DeviceLayoutNode[];
  /** Mean of all hub center-ys — where the host node is vertically centered. */
  hostCY: number;
  /** Total drawable height, in the same px units as cy/yTop — grows with
   * device count, never a fixed/zero size. */
  height: number;
}

/**
 * Compute vertical positions for the fan-out graph. Guards empty
 * hubs/devices — returns a layout with sane defaults (non-crashing,
 * non-NaN) rather than throwing, since a host may not have reported any
 * USB topology yet.
 */
export function computeTopologyLayout(
  hubs: LayoutHubInput[],
  devices: LayoutDeviceInput[]
): TopologyLayout {
  let cy = START_Y;
  const deviceNodes: DeviceLayoutNode[] = [];
  const hubNodes: HubLayoutNode[] = [];

  for (const hub of hubs) {
    const hubDevices = devices.filter((d) => d.hubId === hub.id);
    const yTopStart = cy;
    const centers: number[] = [];

    if (hubDevices.length === 0) {
      // No known children — still needs a slot so the hub renders at a
      // sane position instead of collapsing to zero height.
      const center = cy + CHIP_H / 2;
      hubNodes.push({ id: hub.id, yTop: yTopStart, cy: center, count: 0 });
      cy += CHIP_H + GROUP_GAP;
      continue;
    }

    for (const d of hubDevices) {
      const yTop = cy;
      const center = yTop + CHIP_H / 2;
      deviceNodes.push({ serial: d.serial, hubId: hub.id, yTop, cy: center });
      centers.push(center);
      cy += CHIP_H + CHIP_GAP;
    }

    const hubCY = centers.reduce((a, b) => a + b, 0) / centers.length;
    hubNodes.push({ id: hub.id, yTop: yTopStart, cy: hubCY, count: hubDevices.length });
    cy += GROUP_GAP;
  }

  const hostCY =
    hubNodes.length > 0
      ? hubNodes.reduce((a, h) => a + h.cy, 0) / hubNodes.length
      : START_Y;

  // Trailing GROUP_GAP already added after the last hub; floor at a minimum
  // so an empty graph never reports a degenerate/zero height.
  const height = Math.max(cy, START_Y * 2);

  return { hubs: hubNodes, devices: deviceNodes, hostCY, height };
}
